import React, { useCallback, useEffect, useState } from "react";
import { useHeaderAtom } from "@RHCommerceDev/hooks/atoms/useHeaderAtom";
import useGetNavigation from "@RHCommerceDev/hooks/useGetNavigation";
import { useLocalization } from "@RHCommerceDev/hooks/useLocalization";
import { useScrollLock } from "@RHCommerceDev/hooks/useScrollLock";
import { useAppId } from "@RHCommerceDev/hooks/useAppId";
import parsedCatalogNavigation, {
  groupChildCategories
} from "@RHCommerceDev/utils/parseCatalogNavigationData";
import memoize from "@RHCommerceDev/utils/memoize";
import NavigationDropdown from "@RHCommerceDev/container-rhr-header/CatalogNavigation/NavigationDropdown";
import useIsoRedirect from "@RHCommerceDev/hooks/useIsoRedirect";
import {
  BRAND_NAME_TO_BRAND_CODE,
  CATALOG_SALE_NAME,
  PG_SEARCH_TERM,
  SELECTED_BRAND_COOKIE
} from "@RHCommerceDev/utils/constants";
import buildPath, { isForcedReloadPath } from "@RHCommerceDev/utils/buildPath";
import { useHistory } from "react-router";
import { processEnvServer } from "@RHCommerceDev/hooks/useSsrHooks";
import { duplicatePattern } from "@RHCommerceDev/utils/regex";
import { isSSRToggledCheck } from "@RHCommerceDev/utils/ssrHelpers";
import parseUrlPath from "@RHCommerceDev/utils/parse-url-path";
import useAppData from "@RHCommerceDev/hooks/useAppData";
import { useCookies } from "react-cookie";
import { useEnv } from "@RHCommerceDev/hooks/useEnv";
import { usePreloadImages } from "@RHCommerceDev/hooks/usePreloadImages";
import {
  CG_PREVIEW_ID_COOKIE,
  PG_PREVIEW_ID_COOKIE,
  PP_PREVIEW_ID_COOKIE
} from "pages/NextgenProductGallery/constants";

const NavigationDropdownContainer = ({
  navigateToUrl
}: {
  navigateToUrl?: (navigationItem: NavigationElement) => void | undefined;
}) => {
  const { catalogNavigation, saleCatalogNavigation } = useGetNavigation();
  const locale = useLocalization();
  const { isConcierge } = useAppId();

  const isSSRToggled = isSSRToggledCheck();
  const { getResult } = isForcedReloadPath("");
  const { app } = useAppData();
  const history = useHistory();
  const [, setCookie] = useCookies([SELECTED_BRAND_COOKIE]);
  const [, , removeCookie] = useCookies([
    PG_PREVIEW_ID_COOKIE,
    PP_PREVIEW_ID_COOKIE,
    CG_PREVIEW_ID_COOKIE
  ]);

  const env = useEnv();

  const { state: headerState, closeCategoryDropdown } = useHeaderAtom();
  const isCategoryDropdownOpen = headerState.selectedCategoryIndex !== -1;

  // Parse navigation into structured categories
  const navigationData = parsedCatalogNavigation(
    catalogNavigation,
    saleCatalogNavigation,
    isConcierge
  );
  const categories = navigationData?.childCategories ?? [];

  const [selectedPrimaryIndex, setSelectedPrimaryIndex] = useState<number>(
    isConcierge ? 0 : -1
  );

  const selectedCategory = categories[headerState.selectedCategoryIndex];

  const selectedPrimaryItem =
    selectedCategory?.childCategories?.[selectedPrimaryIndex];
  const groupedCategories = groupChildCategories(
    selectedPrimaryItem?.childCategories
  );

  const clearPreviewCookies = useCallback(() => {
    const previewCookies: any[] = [
      PG_PREVIEW_ID_COOKIE,
      PP_PREVIEW_ID_COOKIE,
      CG_PREVIEW_ID_COOKIE
    ];

    previewCookies.forEach(cookieName => {
      removeCookie(cookieName, { path: "/", domain: env.ENV_DOMAIN });
    });
  }, [removeCookie, env.ENV_DOMAIN]);

  const fireAnalytics = (
    event?: React.MouseEvent,
    selectedCategory?: NavigationElement,
    primaryItem?: NavigationElement,
    currItem?: NavigationElement,
    groupHeader?: NavigationElement | null
  ) => {
    if (!processEnvServer) {
      // Dispatch analytics event
      const target = event?.target as HTMLElement;
      const { id, className } = target;
      document?.body?.dispatchEvent(
        new CustomEvent("cta_click", {
          detail: {
            item: {
              isHeaderNavigationNew: true,
              topCat: selectedCategory?.displayName,
              parentCat: primaryItem?.displayName,
              cat: groupHeader
                ? groupHeader?.displayName
                : currItem?.displayName,
              catURL: groupHeader ? currItem?.displayName : currItem?.targetUrl,
              class: className,
              id: id
            }
          }
        })
      );
    }
  };

  const handlePrimaryColumnButtonClick = useCallback((primaryIndex: number) => {
    setSelectedPrimaryIndex(primaryIndex);
  }, []);

  const handlePrimaryColumnLinkClick = useCallback(
    (event: React.MouseEvent, primaryItem: NavigationElement) => {
      clearPreviewCookies();

      const isSelectedCategorySale =
        selectedCategory?.displayName?.toLowerCase() === CATALOG_SALE_NAME;

      if (isConcierge) {
        if (isSelectedCategorySale) {
          if (primaryItem?.linkType === "anchor") {
            if (typeof window !== "undefined") {
              const brand =
                BRAND_NAME_TO_BRAND_CODE?.[
                  primaryItem?.id?.toUpperCase?.() ?? "RH"
                ];

              if (brand) {
                setCookie(SELECTED_BRAND_COOKIE, brand, {
                  domain: `${env.ENV_DOMAIN}`,
                  path: "/"
                });
              }
              const url = locale?.endsWith("/") ? "sale" : "/sale";
              window.location.href = `${locale}${url}`;
            }
          } else {
            history.push(buildPath(`${locale}${primaryItem?.targetUrl}`));
          }
        } else {
          // useIsoRedirect(primaryItem?.targetUrl ?? "", "windowAssign");
          return navigateToUrl?.(primaryItem);
        }
      } else {
        fireAnalytics(event, selectedCategory, primaryItem, primaryItem);

        if (isSelectedCategorySale) {
          if (primaryItem?.linkType === "anchor") {
            if (typeof window !== "undefined") {
              const formattedUrl = primaryItem?.targetUrl?.startsWith(
                "https://"
              )
                ? primaryItem?.targetUrl
                : `https://${primaryItem?.targetUrl}`;
              window.open(formattedUrl, "_blank");
            }
          } else {
            history.push(buildPath(`${locale}${primaryItem?.targetUrl}`));
          }
        } else {
          const url = `${locale}${primaryItem?.targetUrl ?? ""}`;
          if (isSSRToggled || getResult(url)) {
            window.location.href = parseUrlPath(url);
          } else {
            history.push(url);
          }
        }
      }

      //close dropdown after navigation
      closeCategoryDropdown();
    },
    [
      getResult,
      isSSRToggled,
      env.ENV_DOMAIN,
      history,
      isConcierge,
      locale,
      selectedCategory,
      setCookie,
      navigateToUrl,
      closeCategoryDropdown,
      clearPreviewCookies
    ]
  );

  const handleSecondaryColumnLinkClick = useCallback(
    (
      event: React.MouseEvent,
      secondaryItem: NavigationElement,
      groupHeader?: NavigationElement | null
    ) => {
      clearPreviewCookies();

      const isSelectedCategorySale =
        selectedCategory?.displayName?.toLowerCase() === CATALOG_SALE_NAME;

      if (isConcierge) {
        if (isSelectedCategorySale) {
          fireAnalytics(
            event,
            selectedCategory,
            selectedPrimaryItem,
            secondaryItem
          );

          if (secondaryItem?.linkType === "anchor") {
            if (typeof window !== "undefined") {
              const formattedUrl = secondaryItem?.targetUrl?.startsWith(
                "https://"
              )
                ? secondaryItem?.targetUrl
                : `https://${secondaryItem?.targetUrl}`;
              window.open(formattedUrl, "_blank");
            }
          } else {
            history.push(buildPath(`${locale}${secondaryItem?.targetUrl}`));
          }
        } else {
          // useIsoRedirect(secondaryItem?.targetUrl ?? "", "windowAssign");
          return navigateToUrl?.(secondaryItem);
        }
      } else {
        fireAnalytics(
          event,
          selectedCategory,
          selectedPrimaryItem,
          secondaryItem,
          groupHeader
        );
        if (isSelectedCategorySale) {
          if (secondaryItem?.linkType === "anchor") {
            if (typeof window !== "undefined") {
              const formattedUrl = secondaryItem?.targetUrl?.startsWith(
                "https://"
              )
                ? secondaryItem?.targetUrl
                : `https://${secondaryItem?.targetUrl}`;
              window.open(formattedUrl, "_blank");
            }
          } else {
            history.push(buildPath(`${locale}${secondaryItem?.targetUrl}`));
          }
        } else {
          // Handle disabled link
          if (secondaryItem.linkType === "none") {
            return useIsoRedirect(secondaryItem?.targetUrl ?? "", "windowOpen");
          }

          // Handle "room" URLs
          let updatedNavItem: string | undefined;
          if (secondaryItem.targetUrl?.includes("room")) {
            updatedNavItem = `${locale}${
              secondaryItem.targetUrl.split("&")[0] // remove params after &
            }`;
          }

          // Handle external URLs
          if (secondaryItem.targetUrl?.startsWith("http")) {
            return useIsoRedirect(secondaryItem.targetUrl, "windowAssign");
          }

          // Handle internal URLs
          let targetUrl = secondaryItem.targetUrl ?? "";

          // Remove duplicate matches (SR-2909 fix)
          const removeDuplicateMatches = (url: string, pattern: RegExp) => {
            const matches = url.match(pattern);
            return matches && matches.length > 1
              ? url.replace(matches[0], "")
              : url;
          };
          targetUrl = removeDuplicateMatches(targetUrl, duplicatePattern);

          const isPGPage = targetUrl.includes("/catalog/category/products.jsp");
          const searchParams = new URLSearchParams(targetUrl);

          if (isPGPage && !searchParams.has(PG_SEARCH_TERM)) {
            searchParams.set(PG_SEARCH_TERM, `category:${secondaryItem.id}`);
            targetUrl = decodeURIComponent(searchParams.toString());
          }

          const basePath =
            updatedNavItem && updatedNavItem.includes("room")
              ? updatedNavItem
              : buildPath(`${locale}${targetUrl}`, {
                  sale: app.saleContextFilter,
                  fromNav:
                    targetUrl.includes("new-results") ||
                    targetUrl.includes("results")
                      ? "true"
                      : null
                });

          // Final navigation
          if (isSSRToggled || getResult(basePath)) {
            window.location.href = parseUrlPath(basePath);
          } else {
            history.push(basePath);
          }
        }
      }

      //close dropdown after navigation
      closeCategoryDropdown();
    },
    [
      app.saleContextFilter,
      getResult,
      history,
      isConcierge,
      isSSRToggled,
      locale,
      selectedCategory,
      selectedPrimaryItem,
      navigateToUrl,
      closeCategoryDropdown,
      clearPreviewCookies
    ]
  );

  const imgPublicUrl = (env.PUBLIC_URL || "").replace(new RegExp(/\/?$/gm), "");

  // Preload Waterworks logo image
  usePreloadImages(["/img/WaterworksLogoWhite.svg"]);

  // used to lock and unlock body scroll
  const { lock, unlock } = useScrollLock({
    autoLock: false,
    widthReflow: false
  });

  useEffect(() => {
    if (isCategoryDropdownOpen) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      lock();
    } else {
      unlock();
    }
  }, [isCategoryDropdownOpen, isConcierge, lock, unlock]);

  //reset primary index on category change
  useEffect(() => {
    setSelectedPrimaryIndex(-1);
  }, [headerState.selectedCategoryIndex]);

  const dropdownHeight = headerState?.headerRect
    ? `calc(100% - ${headerState?.headerRect?.height}px)`
    : undefined;

  return (
    <NavigationDropdown
      isConcierge={isConcierge}
      selectedCategory={selectedCategory}
      groupedCategories={groupedCategories}
      selectedPrimaryIndex={selectedPrimaryIndex}
      onPrimaryColumnButtonClick={handlePrimaryColumnButtonClick}
      onPrimaryColumnLinkClick={handlePrimaryColumnLinkClick}
      onSecondaryColumnLinkClick={handleSecondaryColumnLinkClick}
      waterWorksUrl={`${imgPublicUrl}/img/WaterworksLogoWhite.svg`}
      dropdownHeight={dropdownHeight}
      selectedCategoryIndex={headerState.selectedCategoryIndex}
      closeCategoryDropdown={closeCategoryDropdown}
    />
  );
};

export const MemoizedNavigationDropdownContainer = memoize(
  NavigationDropdownContainer
);
